import { Paperclip, Pencil, Plus, Send, X } from "lucide-react";
import { useId, useRef, useState, type ChangeEvent, type FC } from "react";
import { toast } from "sonner";

import { ForeignNationalDocumentsAPI } from "@/api/foreignNationalDocumentsApi";
import { LoadingState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { ClientPortalAPI, portalErrorCode } from "../api";
import { useClientPortal } from "../components/ClientPortalShell";
import { PortalCard } from "../components/PortalParts";
import { usePortalT } from "../i18n";
import type { PortalStrings } from "../i18n/en";
import {
  ABOUT_TOPICS,
  ADDRESS_PARTS,
  CONTACT_TOPICS,
  MARITAL_STATUSES,
  TOPIC_FIELDS,
  correctionFor,
  displayValue,
  draftFrom,
  formatAddress,
  historyPeriod,
  isAddressTopic,
  pick,
  proposedFrom,
  reviewerName,
  type CorrectionDraft
} from "../profileModel";
import { refreshClientPortal, useClientPortalProfile } from "../queries";
import type {
  CorrectionTopic,
  PortalCorrection,
  PortalProfile
} from "../types";
import { PortalLoadError } from "./PortalHomePage";

type Attachment = { id: string; name: string };
type FieldKey = keyof PortalStrings["profile"]["field"];

const useLabels = () => {
  const { t, i18n } = usePortalT();
  const marital = (status: string) =>
    (MARITAL_STATUSES as readonly string[]).includes(status)
      ? t(`profile.marital.${status as (typeof MARITAL_STATUSES)[number]}`)
      : status;
  const show = (topic: CorrectionTopic, values: Record<string, unknown>) =>
    displayValue(topic, values, i18n.language, marital);
  return { t, show };
};

const DraftInputs: FC<{
  topic: CorrectionTopic;
  draft: CorrectionDraft;
  onChange: (draft: CorrectionDraft) => void;
}> = ({ topic, draft, onChange }) => {
  const { t } = usePortalT();
  const id = useId();
  const set = (key: string) => (value: string) =>
    onChange({ ...draft, [key]: value });
  const input = (
    key: string,
    props: { type?: string; wide?: boolean } = {}
  ) => (
    <span
      key={key}
      className={cn("flex flex-col gap-1.5", props.wide && "sm:col-span-2")}
    >
      <Label htmlFor={`${id}-${key}`}>
        {t(`profile.field.${key as FieldKey}`)}
      </Label>
      <Input
        id={`${id}-${key}`}
        type={props.type ?? "text"}
        value={draft[key] ?? ""}
        onChange={(e) => set(key)(e.target.value)}
      />
    </span>
  );

  if (isAddressTopic(topic)) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {ADDRESS_PARTS.map((part) => input(part, { wide: part === "street" }))}
      </div>
    );
  }
  if (topic === "marital_status") {
    return (
      <span className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-marital`}>
          {t("profile.field.marital_status")}
        </Label>
        <Select
          value={draft.marital_status || undefined}
          onValueChange={set("marital_status")}
        >
          <SelectTrigger id={`${id}-marital`} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MARITAL_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {t(`profile.marital.${status}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </span>
    );
  }
  const fields = TOPIC_FIELDS[topic];
  const type =
    topic === "birthday"
      ? "date"
      : topic === "email"
      ? "email"
      : topic === "phone"
      ? "tel"
      : "text";
  return (
    <div className={cn("grid gap-3", fields.length === 3 && "sm:grid-cols-3")}>
      {fields.map((field) => input(field, { type }))}
    </div>
  );
};

const CorrectionForm: FC<{
  topic: CorrectionTopic;
  current: Record<string, unknown>;
  reviewer: string;
  onDone: () => void;
}> = ({ topic, current, reviewer, onDone }) => {
  const { t } = usePortalT();
  const { client } = useClientPortal();
  const id = useId();
  const [draft, setDraft] = useState(() => draftFrom(topic, current));
  const [reason, setReason] = useState("");
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const history = topic === "history";

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(file.name);
    ForeignNationalDocumentsAPI.upload(client.uid, file)
      .then((doc) =>
        setAttachment({ id: doc.foreign_national_document_id, name: file.name })
      )
      .catch(() => toast.error(t("profile.attachFailed")))
      .finally(() => setUploading(null));
  };

  const send = () => {
    const proposed = history ? undefined : proposedFrom(topic, draft, current);
    if (proposed && !Object.keys(proposed).length) {
      setProblem(t("profile.unchanged"));
      return;
    }
    if (history && !reason.trim()) {
      setProblem(t("profile.historyLabel"));
      return;
    }
    setSending(true);
    setProblem(null);
    ClientPortalAPI.suggestCorrection({
      topic,
      proposed,
      reason: reason.trim() || undefined,
      document_id: attachment?.id ?? null
    })
      .then(() => {
        toast.success(t("profile.sent", { name: reviewer }));
        onDone();
        return refreshClientPortal();
      })
      .catch((error: unknown) =>
        setProblem(
          portalErrorCode(error) === "unchanged"
            ? t("profile.unchanged")
            : t("profile.failed")
        )
      )
      .finally(() => setSending(false));
  };

  return (
    <div
      className="mt-1 flex flex-col gap-3 rounded-xl border border-primary/20 p-3.5 ring-3 ring-primary/10"
      data-testid={`correction-form-${topic}`}
    >
      {history ? null : (
        <DraftInputs topic={topic} draft={draft} onChange={setDraft} />
      )}
      <span className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-reason`}>
          {history
            ? t("profile.historyLabel")
            : t("profile.note", { name: reviewer })}
        </Label>
        <Textarea
          id={`${id}-reason`}
          rows={history ? 3 : 2}
          value={reason}
          placeholder={
            history
              ? t("profile.historyPlaceholder")
              : isAddressTopic(topic)
              ? t("profile.addressPlaceholder")
              : t("profile.notePlaceholder")
          }
          onChange={(e) => setReason(e.target.value)}
        />
      </span>
      {problem ? (
        <p className="text-sm text-destructive" role="alert">
          {problem}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {attachment ? (
          <span className="flex h-8 max-w-full items-center gap-1.5 rounded-md border border-dashed border-border px-2.5 text-xs">
            <Paperclip className="size-3.5 shrink-0" />
            <span className="truncate">{attachment.name}</span>
            <Button
              size="icon-xs"
              variant="ghost"
              aria-label={t("profile.removeAttachment")}
              onClick={() => setAttachment(null)}
            >
              <X />
            </Button>
          </span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            disabled={Boolean(uploading)}
            title={t("profile.attachHint")}
            onClick={() => fileInput.current?.click()}
          >
            <Paperclip />
            {uploading
              ? t("profile.attaching", { name: uploading })
              : t("profile.attach")}
          </Button>
        )}
        <input
          ref={fileInput}
          type="file"
          hidden
          accept="application/pdf,image/*"
          onChange={onFile}
        />
        <span className="flex-1" />
        <Button size="sm" variant="ghost" onClick={onDone}>
          {t("profile.cancel")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={sending || Boolean(uploading)}
          onClick={send}
          data-testid="correction-send"
        >
          <Send />
          {sending
            ? t("profile.sending")
            : t("profile.send", { name: reviewer })}
        </Button>
      </div>
    </div>
  );
};

const Waiting: FC<{ correction: PortalCorrection; reviewer: string }> = ({
  correction,
  reviewer
}) => {
  const { t } = usePortalT();
  const [busy, setBusy] = useState(false);
  const { preview } = useClientPortal();
  const withdraw = () => {
    setBusy(true);
    ClientPortalAPI.withdrawCorrection(correction.correction_id)
      .then(() => {
        toast.success(t("profile.withdrawn"));
        return refreshClientPortal();
      })
      .catch(() => toast.error(t("profile.failed")))
      .finally(() => setBusy(false));
  };
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="rounded-full bg-warn-soft px-2 py-0.5 text-xs font-medium text-warn">
        {t("profile.waiting", { name: reviewer })}
      </span>
      {preview ? null : (
        <Button
          size="xs"
          variant="link"
          className="h-auto px-0 text-muted-foreground"
          disabled={busy}
          onClick={withdraw}
        >
          {t("profile.withdraw")}
        </Button>
      )}
    </span>
  );
};

const Decided: FC<{ correction: PortalCorrection; reviewer: string }> = ({
  correction,
  reviewer
}) => {
  const { t } = usePortalT();
  if (correction.status === "accepted") {
    return (
      <span className="w-fit rounded-full bg-success-soft px-2 py-0.5 text-xs font-medium text-success">
        {t("profile.updated")}
      </span>
    );
  }
  return (
    <span className="text-sm text-muted-foreground">
      {correction.decline_reason
        ? t("profile.keptWhy", {
            name: reviewer,
            reason: correction.decline_reason
          })
        : t("profile.kept", { name: reviewer })}
    </span>
  );
};

const TopicRow: FC<{
  topic: CorrectionTopic;
  client: Record<string, unknown>;
  correction: PortalCorrection | undefined;
  reviewer: string;
  editing: boolean;
  onEdit: (topic: CorrectionTopic | null) => void;
}> = ({ topic, client, correction, reviewer, editing, onEdit }) => {
  const { t, show } = useLabels();
  const { preview } = useClientPortal();
  const current = pick(topic, client);
  const value = show(topic, current);
  const pending = correction?.status === "pending" ? correction : undefined;
  const proposed = pending
    ? show(topic, { ...current, ...pending.proposed })
    : "";
  return (
    <div
      className="flex flex-col gap-1 border-t border-line-2 py-3 first:border-t-0 first:pt-0 sm:flex-row sm:items-start sm:gap-4"
      data-testid={`profile-row-${topic}`}
    >
      <span className="text-sm text-muted-foreground sm:w-44 sm:shrink-0 sm:pt-px">
        {t(`profile.topic.${topic}`)}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <span
          className={cn(
            "text-md break-words",
            !value && "text-muted-foreground",
            (pending || editing) && "text-muted-foreground",
            pending && value && "line-through"
          )}
        >
          {value || t("profile.notOnFile")}
        </span>
        {pending ? (
          <>
            <span className="text-md font-medium break-words">{proposed}</span>
            <Waiting correction={pending} reviewer={reviewer} />
          </>
        ) : correction && !editing ? (
          <Decided correction={correction} reviewer={reviewer} />
        ) : null}
        {editing ? (
          <CorrectionForm
            topic={topic}
            current={current}
            reviewer={reviewer}
            onDone={() => onEdit(null)}
          />
        ) : null}
      </span>
      {pending || editing ? null : (
        <Button
          size="sm"
          variant="ghost"
          className="w-fit self-start px-2 text-primary sm:-mt-1"
          disabled={preview}
          title={preview ? t("profile.previewOnly") : undefined}
          onClick={() => onEdit(topic)}
        >
          {value ? <Pencil /> : <Plus />}
          {value ? t("profile.suggest") : t("profile.add")}
        </Button>
      )}
    </div>
  );
};

const HistoryList: FC<{
  title: string;
  rows: { id: string; primary: string; secondary: string; period: string }[];
}> = ({ title, rows }) => {
  const { t } = usePortalT();
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">{title}</h3>
      {rows.length ? (
        <ul className="flex flex-col">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex flex-col gap-0.5 border-t border-line-2 py-2.5 first:border-t-0 first:pt-0 sm:flex-row sm:gap-4"
            >
              <span className="text-sm text-muted-foreground sm:w-44 sm:shrink-0">
                {row.period}
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="text-md break-words">{row.primary}</span>
                {row.secondary ? (
                  <span className="text-sm text-muted-foreground">
                    {row.secondary}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <span className="text-sm text-muted-foreground">
          {t("profile.noneOnFile")}
        </span>
      )}
    </div>
  );
};

const HistoryCard: FC<{
  profile: PortalProfile;
  reviewer: string;
  editing: boolean;
  onEdit: (topic: CorrectionTopic | null) => void;
}> = ({ profile, reviewer, editing, onEdit }) => {
  const { t, i18n } = usePortalT();
  const { preview } = useClientPortal();
  const when = (row: {
    start: string | null;
    end: string | null;
    current: boolean;
  }) => historyPeriod(row, i18n.language, t("profile.present"));
  const { addresses, employment, education } = profile.histories;
  const notes = profile.corrections.filter((c) => c.topic === "history");
  return (
    <PortalCard title={t("profile.topic.history")}>
      <HistoryList
        title={t("profile.lived")}
        rows={addresses.map((r) => ({
          id: r.id,
          primary: formatAddress(r.address),
          secondary: "",
          period: when(r)
        }))}
      />
      <HistoryList
        title={t("profile.worked")}
        rows={employment.map((r) => ({
          id: r.id,
          primary: r.employer ?? r.title ?? "",
          secondary: r.employer ? r.title ?? "" : "",
          period: when(r)
        }))}
      />
      <HistoryList
        title={t("profile.studied")}
        rows={education.map((r) => ({
          id: r.id,
          primary: r.institution ?? r.degree ?? "",
          secondary: [r.degree, r.field].filter(Boolean).join(", "),
          period: when(r)
        }))}
      />
      {notes.length ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-medium">{t("profile.yourNotes")}</h3>
          {notes.map((note) => (
            <div
              key={note.correction_id}
              className="flex flex-col gap-1.5 rounded-lg bg-fill-1 p-3"
            >
              <span className="text-sm whitespace-pre-line">{note.reason}</span>
              {note.status === "pending" ? (
                <Waiting correction={note} reviewer={reviewer} />
              ) : (
                <Decided correction={note} reviewer={reviewer} />
              )}
            </div>
          ))}
        </div>
      ) : null}
      {editing ? (
        <CorrectionForm
          topic="history"
          current={{}}
          reviewer={reviewer}
          onDone={() => onEdit(null)}
        />
      ) : (
        <span className="flex flex-wrap items-center gap-1 border-t border-line-2 pt-3 text-sm text-muted-foreground">
          {t("profile.historyHint")}
          <Button
            size="sm"
            variant="link"
            className="h-auto px-1"
            disabled={preview}
            title={preview ? t("profile.previewOnly") : undefined}
            onClick={() => onEdit("history")}
          >
            {t("profile.tellUs", { name: reviewer })}
          </Button>
        </span>
      )}
    </PortalCard>
  );
};

export const PortalProfilePage: FC = () => {
  const me = useClientPortal();
  const { t } = usePortalT();
  const profile = useClientPortalProfile();
  const [editing, setEditing] = useState<CorrectionTopic | null>(null);
  const reviewer = reviewerName(me.team, t("explainer.yourTeam"));

  const section = (
    title: string,
    topics: CorrectionTopic[],
    data: PortalProfile
  ) => {
    const client = data.client as Record<string, unknown>;
    return (
      <PortalCard title={title}>
        <div className="flex flex-col">
          {topics.map((topic) => (
            <TopicRow
              key={topic}
              topic={topic}
              client={client}
              correction={correctionFor(data.corrections, topic)}
              reviewer={reviewer}
              editing={editing === topic}
              onEdit={setEditing}
            />
          ))}
        </div>
      </PortalCard>
    );
  };

  return (
    <>
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("shell.nav.profile")}
        </h1>
        <span className="text-md text-muted-foreground">
          {t("profile.subtitle", {
            name:
              reviewer === t("explainer.yourTeam")
                ? t("explainer.yourTeamLower")
                : reviewer
          })}
        </span>
      </header>
      {profile.isLoading ? <LoadingState size="lg" /> : null}
      {!profile.isLoading && !profile.data ? (
        <PortalLoadError onRetry={() => void profile.refetch()} />
      ) : null}
      {profile.data ? (
        <>
          {section(t("profile.about"), ABOUT_TOPICS, profile.data)}
          {section(t("profile.contact"), CONTACT_TOPICS, profile.data)}
          <HistoryCard
            profile={profile.data}
            reviewer={reviewer}
            editing={editing === "history"}
            onEdit={setEditing}
          />
        </>
      ) : null}
    </>
  );
};
